import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "./InvoiceSchemasScreen.module.scss";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import ResourceList from "../../../components/resourceList/ResourceList";
import Button from "../../../components/button/Button";
import Input from "../../../components/input/Input";
import Checkbox from "../../../components/checkbox/Checkbox";
import moment from "moment";
import billingInvoicesService from "../../../apis/services/admin/billingInvoicesService";
import AreYouSureModal from "../../../components/modals/areYouSureModal/AreYouSureModal";
import BulkCreateInvoicesModal from "../../../components/modals/bulkCreateInvoicesModal/BulkCreateInvoicesModal";
import _ from "lodash";
import {
  selectInvoiceSchema,
  selectAllInvoiceSchemas,
  fetchInvoiceSchemaIds,
} from "../../../store/actions/admin/billingInvoices";

const HEADER_VALUES = [
  { label: "", key: "checkbox", sortable: false },
  { label: "NAZIV", key: "name", sortable: true },
  { label: "OPIS", key: "description", sortable: false },
  { label: "FAKTURISKA KOMPANIJA", key: "billingCompanyName", sortable: false },
  { label: "KOMPANIJA", key: "companyName", sortable: false },
  { label: "TIP PERIODA", key: "defaultBillingPeriodType", sortable: false },
  { label: "POPUST KOLIČINA", key: "discountQuantity", sortable: false },
  { label: "AKTIVNA", key: "isActive", sortable: false },
  { label: "STAVKE", key: "itemsCount", sortable: false },
  { label: "KREIRANO", key: "createdAt", sortable: true },
  { label: "AKCIJE", sortable: false },
];

const InvoiceSchemasScreen = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [schemas, setSchemas] = useState({
    page: 1,
    perPage: 20,
    total: 0,
    data: [],
    sortBy: "id",
    order: "DESC",
    attempt: false,
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bulkCreateModalOpen, setBulkCreateModalOpen] = useState(false);
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const sortRef = useRef({ sortBy: "id", order: "DESC" });
  const searchRef = useRef("");

  const selectedSchemas = useSelector((state) => state.billingInvoices.selectedSchemas.data);
  const selectAll = useSelector((state) => state.billingInvoices.selectedSchemas.selectAll);
  const allSchemaIds = useSelector((state) => state.billingInvoices.selectedSchemas.allSchemaIds);
  const schemaIdsData = useSelector((state) => state.billingInvoices.schemaIds);

  const loadSchemas = useCallback(async (page = 1, sortBy = "id", order = "DESC", search = "") => {
    sortRef.current = { sortBy, order };
    searchRef.current = search;
    setSchemas((prev) => ({ ...prev, attempt: true }));
    setLoading(true);
    try {
      const response = await billingInvoicesService.getInvoiceSchemas(page, 20, sortBy, order, search);
      // Handle nested response structure: response.data.data.data
      const schemasData = response.data?.data?.data || response.data?.data || response.data || [];
      
      // Transform data to match ResourceList format
      const transformedData = Array.isArray(schemasData) ? schemasData : [];
      
      // Extract pagination info from response
      const responsePage = response.data?.data?.page || response.data?.page || page;
      const responsePerPage = response.data?.data?.perPage || response.data?.perPage || 20;
      const responseTotal = response.data?.data?.total || response.data?.total || transformedData.length;
      
      console.log("Invoice Schemas Response:", {
        page: responsePage,
        perPage: responsePerPage,
        total: responseTotal,
        dataLength: transformedData.length,
        fullResponse: response.data
      });
      
      setSchemas({
        page: responsePage,
        perPage: responsePerPage,
        total: responseTotal,
        data: transformedData,
        sortBy,
        order,
        attempt: false,
      });
    } catch (error) {
      console.error("Error loading invoice schemas:", error);
      toast.error("Greška pri učitavanju šema faktura");
      setSchemas((prev) => ({
        ...prev,
        data: [],
        attempt: false,
      }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchemas(1, "id", "DESC", "");
    // Try fetching without isActive parameter first to see if that's the issue
    dispatch(fetchInvoiceSchemaIds(null)); // Fetch all schema IDs
  }, [dispatch, loadSchemas]);

  const handleSearch = useCallback(
    _.debounce((searchValue) => {
      loadSchemas(1, sortRef.current.sortBy, sortRef.current.order, searchValue);
    }, 500),
    [loadSchemas]
  );

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    handleSearch(value);
  };

  const handleSort = useCallback((sortBy, order) => {
    loadSchemas(1, sortBy, order, searchRef.current);
  }, [loadSchemas]);

  const handleLoadPage = useCallback((page) => {
    loadSchemas(page, sortRef.current.sortBy, sortRef.current.order, searchRef.current);
  }, [loadSchemas]);

  const handleSelectAll = () => {
    if (selectAll) {
      dispatch(selectAllInvoiceSchemas(false, []));
    } else {
      // Check if schema IDs are still loading
      if (schemaIdsData.attempt) {
        toast.info("Učitavanje šema...");
        return;
      }

      // Try multiple possible response structures
      // API returns: { message: "...", data: { ids: [...], schemas: [...] } }
      // Service returns response.data, so payload is: { message: "...", data: { ids: [...], schemas: [...] } }
      const ids = schemaIdsData.data?.data?.ids || 
                  schemaIdsData.data?.ids || 
                  (schemaIdsData.data?.data?.schemas?.map(s => s.id) || []) ||
                  (schemaIdsData.data?.schemas?.map(s => s.id) || []);
      
      // If still no IDs, fallback to current page schemas
      if (ids.length === 0) {
        const currentPageIds = schemas.data.map(s => s.id);
        if (currentPageIds.length === 0) {
          toast.warning("Nema dostupnih šema za izbor");
          return;
        }
        // Use current page schemas
        dispatch(selectAllInvoiceSchemas(true, currentPageIds));
      } else {
        dispatch(selectAllInvoiceSchemas(true, ids));
      }
    }
  };

  const handleSelectSchema = (schema) => {
    dispatch(selectInvoiceSchema(schema));
  };

  const isSchemaSelected = (schema) => {
    if (selectAll) {
      return allSchemaIds.includes(schema.id);
    }
    return selectedSchemas.some((s) => s.id === schema.id);
  };

  const handleBulkCreate = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const selectedCount = selectAll ? allSchemaIds.length : selectedSchemas.length;
    if (selectedCount === 0) {
      toast.error("Molimo izaberite barem jednu šemu fakture");
      return;
    }
    
    // Open modal instead of navigating
    setBulkCreateModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedSchema) return;

    const currentPage = schemas.page;

    try {
      await billingInvoicesService.deleteInvoiceSchema(selectedSchema.id);
      toast.success("Šema fakture uspešno obrisana");
      setDeleteModalOpen(false);
      setSelectedSchema(null);
      // Reload current page after delete
      loadSchemas(currentPage, sortRef.current.sortBy, sortRef.current.order, searchRef.current);
    } catch (error) {
      console.error("Error deleting invoice schema:", error);
      toast.error("Greška pri brisanju šeme fakture");
    }
  };

  const rowRender = (schema) => {
    return (
      <div key={schema.id} className={styles.tableRow}>
        <div className={styles.tableCell}>
          <Checkbox
            onCheck={() => handleSelectSchema(schema)}
            checked={isSchemaSelected(schema)}
          />
        </div>
        <div className={styles.tableCell}>
          <strong>{schema.name || "-"}</strong>
        </div>
        <div className={styles.tableCell}>
          {schema.description || schema.notes || "-"}
        </div>
        <div className={styles.tableCell}>
          {schema.billingCompany?.name || schema.billingCompanyId || "-"}
        </div>
        <div className={styles.tableCell}>
          {schema.company?.name || schema.companyId || "-"}
        </div>
        <div className={styles.tableCell}>
          {schema.defaultBillingPeriodType === "monthly" && "Mesečno"}
          {schema.defaultBillingPeriodType === "quarterly" && "Kvartalno"}
          {schema.defaultBillingPeriodType === "semi-annual" && "Polugodišnje"}
          {schema.defaultBillingPeriodType === "annual" && "Godišnje"}
          {!schema.defaultBillingPeriodType && "-"}
        </div>
        <div className={styles.tableCell}>
          {schema.discountQuantity || "0"}
        </div>
        <div className={styles.tableCell}>
          <span className={schema.isActive ? styles.activeBadge : styles.inactiveBadge}>
            {schema.isActive ? "Da" : "Ne"}
          </span>
        </div>
        <div className={styles.tableCell}>
          {schema.invoiceSchemaItems?.length || 0}
        </div>
        <div className={styles.tableCell}>
          {schema.createdAt ? moment(schema.createdAt).format("DD.MM.YYYY") : "-"}
        </div>
        <div className={styles.tableCell}>
          <button
            className={styles.viewButton}
            onClick={() => history.push(`/invoice-schemas/${schema.id}`)}
          >
            Detalji
          </button>
          <button
            className={styles.deleteButton}
            onClick={() => {
              setSelectedSchema(schema);
              setDeleteModalOpen(true);
            }}
          >
            Obriši
          </button>
        </div>
      </div>
    );
  };

  const selectedCount = selectAll ? allSchemaIds.length : selectedSchemas.length;

  return (
    <DashboardLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Šeme faktura</h1>
          <div className={styles.headerActions}>
            {selectedCount > 0 && (
              <Button
                name={`Masovno kreiranje faktura (${selectedCount})`}
                onClick={handleBulkCreate}
              />
            )}
            <Button
              name="+ Nova šema"
              onClick={() => history.push("/invoice-schemas/new")}
            />
          </div>
        </div>

        <div className={styles.searchWrapper}>
          <Input
            placeholder="Pretraži šeme faktura..."
            onChange={handleSearchChange}
            value={searchTerm}
          />
        </div>

        {selectedCount > 0 && (
          <div className={styles.selectedInfo}>
            <p>Izabrano: {selectedCount} šema</p>
          </div>
        )}

        <ResourceList
          resource={{
            page: schemas.page || 1,
            perPage: schemas.perPage || 20,
            total: schemas.total || 0,
            data: schemas.data || [],
            sortBy: schemas.sortBy || "id",
            order: schemas.order || "DESC",
            attempt: loading,
          }}
          headerValues={HEADER_VALUES}
          selectable={true}
          onSelectAll={handleSelectAll}
          selectAll={selectAll}
          onLoadPage={handleLoadPage}
          onSort={handleSort}
          rowRender={rowRender}
          showLoader={true}
        />
      </div>

      <AreYouSureModal
        modalOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedSchema(null);
        }}
        onConfirm={handleDelete}
        title="Obriši šemu fakture"
        message={`Da li ste sigurni da želite da obrišete šemu fakture "${selectedSchema?.name}"?`}
      />
      <BulkCreateInvoicesModal
        modalOpen={bulkCreateModalOpen}
        onClose={() => setBulkCreateModalOpen(false)}
        selectedSchemas={selectedSchemas}
        selectAll={selectAll}
        allSchemaIds={allSchemaIds}
      />
    </DashboardLayout>
  );
};

export default InvoiceSchemasScreen;

